import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/ds";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";

/**
 * Panel › Cobros — the FixyBee's payment CONTACT handles (T12-M4b, spec
 * screen 11).
 *
 * **Nothing here is money.** A handle is stored, shown, and copied or opened by
 * the client; the payment happens inside the other app and FixyDany processes,
 * records and verifies none of it. There is deliberately no amount, no status
 * and no history on this screen — the shield-check box is a disclaimer, not a
 * feature.
 *
 * The app list comes from the server with the values (`GET
 * /users/me/payment-methods` returns `apps` and `methods`), so adding an app is
 * a deploy and this screen changes nothing.
 *
 * Third-party logos are not used, by instruction: the 32px circle carries the
 * app's initial, as the mockup draws it.
 */
export default function PaymentMethods() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [methods, setMethods] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const role = user?.profile?.role;

  const load = useCallback(() => {
    api.get("/users/me/payment-methods")
      .then((r) => { setApps(r.data.apps || []); setMethods(r.data.methods || []); })
      .catch(() => setError("load"));
  }, []);

  useEffect(() => {
    if (role === "provider") load();
  }, [role, load]);

  if (role && role !== "provider") return <Navigate to="/panel" replace />;

  const byKey = Object.fromEntries(methods.map((m) => [m.app_key, m]));

  const save = async (appKey) => {
    const handle = draft.trim();
    if (!handle) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.put(`/users/me/payment-methods/${appKey}`, { handle });
      setMethods(data.methods || []);
      setEditing(null);
      setDraft("");
    } catch (e) {
      setError(e?.response?.data?.detail || "save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (appKey) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/users/me/payment-methods/${appKey}`);
      setMethods(data.methods || []);
      setEditing(null);
      setDraft("");
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div style={{ display: "grid", gap: 16, maxWidth: 640 }}>
      <p className="text-smoke" style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
        {t("payouts.context")}
      </p>

      <div
        style={{
          background: "var(--paper)", border: "1px solid var(--sand)",
          borderRadius: 16, padding: "0 12px",
        }}
        data-testid="payout-apps"
      >
        {apps.map((app, i) => {
          const mine = byKey[app.key];
          const open = editing === app.key;
          const last = i === apps.length - 1;
          return (
            <div
              key={app.key}
              style={{ borderBottom: last && !open ? "none" : "1px solid var(--linen)" }}
            >
              <div
                style={{
                  minHeight: 60, display: "flex", alignItems: "center", gap: 14,
                  padding: "0 4px",
                }}
                data-testid={`payout-row-${app.key}`}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 32, height: 32, borderRadius: "50%", flex: "none",
                    background: "var(--linen)", border: "1px solid var(--sand)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    font: "700 13px var(--font-mono)", color: "var(--worker)",
                  }}
                >
                  {app.initial}
                </span>
                <span style={{ display: "grid", gap: 1, minWidth: 0, flex: 1 }}>
                  <span style={{ font: "600 16px var(--font-display)", color: "var(--text-body)" }}>
                    {app.label}
                  </span>
                  {mine && !open && (
                    <span
                      style={{
                        font: "500 12px var(--font-mono)", color: "var(--smoke)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                      data-testid={`payout-handle-${app.key}`}
                    >
                      {mine.handle}
                    </span>
                  )}
                </span>

                {!open && (mine ? (
                  <button
                    type="button"
                    onClick={() => { setEditing(app.key); setDraft(mine.handle); }}
                    aria-label={t("payouts.edit", { app: app.label })}
                    data-testid={`payout-edit-${app.key}`}
                    style={{
                      width: 44, height: 44, display: "flex", alignItems: "center",
                      justifyContent: "center", border: "none", background: "transparent",
                      cursor: "pointer", color: "var(--smoke)", marginRight: -10,
                    }}
                  >
                    <Icon name="external-link" size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setEditing(app.key); setDraft(""); }}
                    data-testid={`payout-add-${app.key}`}
                    style={{
                      minHeight: 44, padding: "0 4px", border: "none",
                      background: "transparent", cursor: "pointer",
                      font: "600 14px var(--font-display)", color: "var(--hive-ink)",
                    }}
                  >
                    {t("payouts.add")}
                  </button>
                ))}
              </div>

              {/* The editor. The spec draws the row and says the icon edits the
                  handle, but not what the editing looks like — this is the
                  smallest thing that is one tap from the row it belongs to. */}
              {open && (
                <div style={{ padding: "0 4px 12px", display: "grid", gap: 8 }}>
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") save(app.key); }}
                    maxLength={120}
                    aria-label={t("payouts.handleLabel", { app: app.label })}
                    placeholder={t(`payouts.hint.${app.key}`, { defaultValue: "" })}
                    data-testid={`payout-input-${app.key}`}
                    style={{
                      height: 44, borderRadius: 8, padding: "0 12px",
                      border: "1px solid var(--sand)", background: "var(--canvas)",
                      font: "500 15px var(--font-mono)", color: "var(--worker)",
                      minWidth: 0, width: "100%",
                    }}
                  />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button" onClick={() => save(app.key)} disabled={busy || !draft.trim()}
                      data-testid={`payout-save-${app.key}`}
                      style={{
                        height: 44, padding: "0 18px", borderRadius: 8, border: "none",
                        background: "var(--hive)", color: "var(--worker)", cursor: "pointer",
                        font: "700 15px var(--font-display)",
                        opacity: busy || !draft.trim() ? 0.5 : 1,
                      }}
                    >
                      {t("common.save")}
                    </button>
                    <button
                      type="button" onClick={() => { setEditing(null); setDraft(""); }}
                      style={{
                        height: 44, padding: "0 14px", borderRadius: 8,
                        border: "1px solid var(--sand)", background: "transparent",
                        cursor: "pointer", font: "600 15px var(--font-display)",
                        color: "var(--text-body)",
                      }}
                    >
                      {t("common.cancel")}
                    </button>
                    {mine && (
                      <button
                        type="button" onClick={() => remove(app.key)} disabled={busy}
                        data-testid={`payout-remove-${app.key}`}
                        style={{
                          height: 44, padding: "0 14px", borderRadius: 8, border: "none",
                          background: "transparent", cursor: "pointer",
                          font: "600 15px var(--font-display)", color: "var(--danger)",
                          marginLeft: "auto",
                        }}
                      >
                        {t("common.remove")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p style={{ color: "var(--danger)", fontSize: 14, margin: 0 }} data-testid="payout-error">
          {t("common.error")}
        </p>
      )}

      {/* Dusk is the colour of trust in this system; the box is a disclaimer. */}
      <div
        style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          background: "var(--dusk-tint)", color: "var(--dusk)",
          borderRadius: 16, padding: 14, fontSize: 14, lineHeight: 1.45,
        }}
        data-testid="payout-notice"
      >
        <span style={{ flex: "none", display: "flex", paddingTop: 1 }}>
          <Icon name="shield-check" size={20} />
        </span>
        <span>{t("payouts.notice")}</span>
      </div>
    </div>
  );

  /* Wrapped like every other section of the Panel, so it gets the same two
     things for free and by the same rule: the "‹ Panel" header below 992px,
     and the sidebar above it. */
  return (
    <DashboardLayout role="provider">
      <div data-testid="payment-methods-page">
        <h1
          className="font-display font-bold text-worker nav:block hidden"
          style={{ fontSize: 28, margin: "0 0 4px" }}
        >
          {t("payouts.title")}
        </h1>
        {body}
      </div>
    </DashboardLayout>
  );
}
